// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A control component for displaying and modifying asset ownership.
 * This module provides a clickable interface for viewing the
 * current owner and opening a dialog to change ownership between
 * the organization and its customers.
 *
 * Key features:
 * - Owner display
 * - Dialog integration
 * - Click interaction
 * - Organization fallback
 * - Assignment handling
 */
import { Asset } from '@/asset/api/types';
import { AssetOwnershipDialog } from '@/asset/components/ownership-dialog';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Typography } from '@mui/material';

interface Props {
  /**
   * The asset whose ownership is being managed.
   * Contains current ownership details.
   */
  asset: Asset;
}

/**
 * A control component for asset ownership management.
 * Displays the current owner's name and provides a
 * click interface to open an ownership change dialog.
 * Falls back to the organization name if no customer
 * is assigned.
 *
 * Features:
 * - Current owner display
 * - Click-to-edit behavior
 * - Dialog integration
 * - Organization fallback
 * - Ownership handling
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetOwnershipControl asset={asset} />
 *   );
 * }
 * ```
 */
export function AssetOwnershipControl(props: Props) {
  const dialog = useDisclosure();
  const { user } = useCurrentUser();

  // Determine the current owner - either customer from ownership or organization
  const owner = props.asset.ownership?.customer ?? user.organization;

  return (
    <>
      <AssetOwnershipDialog
        asset={props.asset}
        isOpen={dialog.isOpen}
        onClose={dialog.close}
      />
      <EditablePropertyButton onClick={dialog.open}>
        <Typography variant="body2">{owner.name}</Typography>
      </EditablePropertyButton>
    </>
  );
}
